import type { FeatureEditSession } from '../../../edit/FeatureEditSession';
import { FilletFeatureDefinitionModel } from '../../../definition/fillet/FilletFeatureDefinition';
import { FilletFeaturePrototype } from '../../../prototype/fillet/FilletFeaturePrototype';
import type { FeatureExecutionRuntime } from '../../FeatureExecutionRuntime';
import { FilletCommitExecution } from './FilletCommitExecution';
import {
    FeatureCommitExecutionHandler,
    type FeatureCommitExecutionInput,
    type FeatureCommitExecutionResult,
} from '../FeatureCommitExecutionHandler';

/**
 * 圆角特征提交执行处理器。
 */
export class FilletCommitExecutionHandler extends FeatureCommitExecutionHandler {
    /** 圆角提交执行器。 */
    private readonly execution: FilletCommitExecution;
    /** 特征执行运行时。 */
    private readonly runtime: FeatureExecutionRuntime;

    /**
     * @param execution 圆角提交执行器。
     * @param runtime 执行运行时。
     */
    public constructor(execution: FilletCommitExecution, runtime: FeatureExecutionRuntime) {
        super();
        this.execution = execution;
        this.runtime = runtime;
    }

    /**
     * @inheritdoc
     */
    public supports(input: FeatureCommitExecutionInput): boolean {
        return input.featureKind === 'fillet' && input.definition instanceof FilletFeatureDefinitionModel;
    }

    /**
     * @inheritdoc
     * @throws 当提交输入中的定义不是圆角特征定义时。
     */
    public async execute(input: FeatureCommitExecutionInput): Promise<FeatureCommitExecutionResult> {
        if (!(input.definition instanceof FilletFeatureDefinitionModel)) {
            throw new Error('圆角提交处理器收到的 definition 类型不正确');
        }

        const featureId = input.featureId ?? `feature:${Date.now()}`;
        const prototype = new FilletFeaturePrototype({
            featureId,
            definition: input.definition,
            bindings: input.bindings,
            sourceSketchId: null,
        });

        if (input.session) {
            const replaced = await this.execution.commitEdit(
                input.session as FeatureEditSession<FilletFeatureDefinitionModel, FilletFeaturePrototype>,
                prototype,
            );
            return {
                replaced,
                featureId,
                prototype,
                meshData: null,
            };
        }

        const objectId = await this.execution.commitCreate(prototype);
        const meshData = await this.runtime.generateMeshData(objectId);
        return {
            replaced: false,
            featureId,
            prototype,
            meshData,
            objectId,
        };
    }
}
